import React, { useEffect, useState } from 'react';
import { getDomainSeo, updateDomainSeo } from '../api';
import Alert from '../ui/Alert';
import Spinner from '../ui/Spinner';
import { RULE_FIELDS, fieldId, formFromRules, parseForm, overridesFor } from './seoThin';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const show = (f, v) => (f.type === 'bool' ? (v ? 'yes' : 'no') : f.type === 'list' ? (v || []).join(', ') || 'none' : String(v));

/* Site settings panel: the domain's effective thin-page rules, each marked
   when it is the default, editable, saved as overrides (only the values that
   differ from the defaults are stored). */
export default function SeoThinSettings({ domainId }) {
  const [seo, setSeo] = useState(null);
  const [form, setForm] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDomainSeo(domainId)
      .then((d) => { if (!cancelled) { setSeo(d); setForm(formFromRules(d.thin_rules)); } })
      .catch((err) => { if (!cancelled) setError(errText(err, 'Failed to load SEO settings')); });
    return () => { cancelled = true; };
  }, [domainId]);

  const set = (id, v) => setForm((f) => ({ ...f, [id]: v }));

  const save = async (e) => {
    e.preventDefault();
    const { rules, errors } = parseForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const d = await updateDomainSeo(domainId, overridesFor(rules, seo.defaults));
      setSeo(d);
      setForm(formFromRules(d.thin_rules));
      setSaved('Thin-page rules saved. The site applies them on its next render of each page.');
    } catch (err) {
      setError(errText(err, 'Failed to save SEO settings'));
    } finally {
      setSaving(false);
    }
  };

  if (!seo && !error) return <Spinner label="Loading SEO settings…" />;
  if (!seo) return <Alert type="error">{error}</Alert>;

  const resetField = (f) => set(fieldId(f), formFromRules(seo.defaults)[fieldId(f)]);
  const isDefaultNow = (f) => {
    const { rules } = parseForm({ ...formFromRules(seo.defaults), [fieldId(f)]: form[fieldId(f)] });
    return JSON.stringify(rules[f.group][f.key]) === JSON.stringify(seo.defaults[f.group][f.key]);
  };

  const group = (name, title) => (
    <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
      <legend style={{ fontWeight: 600, marginBottom: 8 }}>{title}</legend>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '4px 16px' }}>
        {RULE_FIELDS.filter((f) => f.group === name).map((f) => {
          const id = fieldId(f);
          const domId = `seo-${f.group}-${f.key}`;
          const marker = isDefaultNow(f)
            ? <span className="badge badge-plain" style={{ marginLeft: 6 }}>default</span>
            : (
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 6, padding: '0 6px' }}
                      onClick={() => resetField(f)}
                      title={`Default: ${show(f, seo.defaults[f.group][f.key])}`}>
                reset to {show(f, seo.defaults[f.group][f.key])}
              </button>
            );
          if (f.type === 'bool') {
            return (
              <div className="field" key={id}>
                <label className="field-inline" htmlFor={domId}>
                  <input id={domId} type="checkbox" checked={!!form[id]} onChange={(e) => set(id, e.target.checked)} />
                  {f.label}{marker}
                </label>
                {f.hint && <span className="hint">{f.hint}</span>}
              </div>
            );
          }
          return (
            <div className="field" key={id}>
              <label htmlFor={domId}>{f.label}{marker}</label>
              <input
                id={domId}
                className={`input${fieldErrors[id] ? ' is-invalid' : ''}${f.type === 'list' ? ' mono' : ''}`}
                inputMode={f.type === 'number' ? 'numeric' : undefined}
                value={form[id] ?? ''}
                onChange={(e) => set(id, e.target.value)}
                aria-invalid={!!fieldErrors[id]}
              />
              {fieldErrors[id]
                ? <span className="error" role="alert">{fieldErrors[id]}</span>
                : f.hint && <span className="hint">{f.hint}</span>}
            </div>
          );
        })}
      </div>
    </fieldset>
  );

  return (
    <form onSubmit={save}>
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <Alert type="success" onDismiss={() => setSaved(null)}>{saved}</Alert>
      <p className="muted" style={{ marginTop: 0 }}>
        Thin pages are hidden from search engines and the sitemap until improved: the site marks them
        <code> noindex, follow</code>, so links on them still count, and visitors still see them. A page is thin
        when its body is too short, it has no meta description or no main image, or it is a hub with too few
        published children; a directory listing is thin until it is claimed or has enough detail to score.
        The Pages list and the Directory show a Thin badge with the reasons.
      </p>
      {group('pages', 'Pages')}
      {group('listings', 'Directory listings')}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save SEO rules'}</button>
      </div>
    </form>
  );
}
